import React from 'react';
import { loadScript } from '@reverbdotcom/commons/src/loadScript';
import {
  core_apimessages_CheckoutGooglePayDetails as GooglePayDetails,
} from '@reverbdotcom/commons/src/gql/graphql';
import { GOOGLE_PAY_ENVIRONMENT } from '@reverbdotcom/env';
import { API_VERSION, GATEWAY } from './googlePayHelpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import windowWrapper from '@reverbdotcom/commons/src/window_wrapper';

/**
 * Countries that require an existing payment method on
 * file that works with Reverb's supported card network.
 *
 * All other countries can proceed to use Google Pay regardless
 * of whether the user has an existing Google Pay payment method.
 */
const EXISTING_PAYMENT_METHOD_REQUIRED_COUNTRIES = [
  'AU',
  'CA',
  'DE',
  'FR',
  'US',
];

interface Props {
  googlePayDetails: GooglePayDetails;
}

export function useGooglePayAvailability({ googlePayDetails }: Props) {
  const { countryCode } = useUser();
  const [isAvailable, setAvailable] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function checkAvailability() {
      const available = await isReadyToPay(googlePayDetails, countryCode);

      setAvailable(available);
      setLoading(false);
    }

    checkAvailability();
  }, [countryCode, googlePayDetails]);

  return {
    isAvailable,
    loading,
  };
}

export async function isReadyToPay(
  googlePayDetails: GooglePayDetails,
  countryCode: string,
) {
  if (!EXISTING_PAYMENT_METHOD_REQUIRED_COUNTRIES.includes(countryCode)) {
    return true;
  }

  const client = await loadClient(googlePayDetails);

  if (!client) {
    return false;
  }

  try {
    const readyToPay = await client.isReadyToPay({
      apiVersion: API_VERSION.apiVersion,
      apiVersionMinor: API_VERSION.apiVersionMinor,
      existingPaymentMethodRequired: true,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: googlePayDetails.cardNetworks,
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: GATEWAY,
              gatewayMerchantId: googlePayDetails.gatewayMerchantId,
            },
          },
        },
      ],
    });

    return !!(readyToPay.result && readyToPay.paymentMethodPresent);
  } catch {
    return false;
  }
}

async function loadClient({ merchantId, merchantName }: GooglePayDetails): Promise<google.payments.api.PaymentsClient> {
  try {
    await loadScript({ id: '__google-pay', src: 'https://pay.google.com/gp/p/js/pay.js' });

    return new windowWrapper.google.payments.api.PaymentsClient({
      environment: GOOGLE_PAY_ENVIRONMENT,
      merchantInfo: {
        merchantId,
        merchantName,
      },
    });
  } catch {
    return null;
  }
}
